/*
 * Copyright (c) 2020 NOOGA DEVELOPMENT, All rights reserved.
 * Author Raman Kremko <raman.kremko@noogadev.com>
 */

import { Epic, ofType } from 'redux-observable';
import { catchError, map, switchMap, mergeMap, exhaustMap, tap, mapTo, takeUntil } from 'rxjs/operators';
import { forkJoin, from, of } from 'rxjs';
import { AxiosError } from 'axios';
import { downloadDataAsFile, parseError } from '../../utils/utils';
import { i18n } from '../translation/i18n';
import { RootState } from '../store.typings';
import {
    DashBoardFinAction,
    DashBoardFinActionType,
    fetchPaymentTypesSuccessAction,
    fetchTurnoversByActivitiesSuccessAction,
    fetchRegistryItemsCountSuccessAction,
    downloadFinChartDataAsExcelSuccessAction,
    downloadFinChartDataAsExcelErrorAction,
    fetchTurnoversByActivitiesErrorAction,
    fetchPaymentTypesErrorAction,
    fetchRegistryItemsCountErrorAction,
} from '../actions/dashBoardFinActions';
import { dashBoardClient } from '../api/dashBoard/dashBoardClient';
import { TurnoversByActivitiesQueryParams } from '../api/dashBoard/dashBoardClient.typings';
import { RegInspectorDashBoardActionType } from '../actions/RegInspectorAcc/RegInspectorDashBoardActions';
import { FinChartsFilesMap } from '../../components/DashboardWitgets/Common/ChartsTyping';
import { CentralInspectorDashBoardActionType } from '../actions/CentralInspectorAcc/CentralInspectorDashBoard';
import { TURNOVERS_FIRST_CATEGORY, TURNOVERS_LIMIT, TURNOVERS_SECOND_CATEGORY } from '../reducers/dashBoardFinReducer';

type DashBoardFinEpic = Epic<DashBoardFinAction, DashBoardFinAction, RootState>;

export const paymentTypesStatEpic: DashBoardFinEpic = (action$) => {
    return action$.pipe(
        ofType(
            DashBoardFinActionType.OPEN_DASHBOARD_FIN_PAGE,
            CentralInspectorDashBoardActionType.OPEN_CENTRAL_INSPECTOR_DASHBOARD_PAGE,
        ),
        switchMap(() => {
            return from(dashBoardClient.getPaymentTypesStat()).pipe(
                map((data) => fetchPaymentTypesSuccessAction(data)),

                catchError((err: AxiosError) =>
                    of(
                        fetchPaymentTypesErrorAction(
                            parseError(err, i18n.t('DashboardScreens.errors.failedToLoadPaymentTypesStat')),
                        ),
                    ),
                ),

                takeUntil(
                    action$.ofType(
                        DashBoardFinActionType.EXIT_DASHBOARD_FIN_PAGE,
                        CentralInspectorDashBoardActionType.EXIT_CENTRAL_INSPECTOR_DASHBOARD_PAGE,
                    ),
                ),
            );
        }),
    );
};

export const turnoversByActivitiesEpic: DashBoardFinEpic = (action$) => {
    return action$.pipe(
        ofType(
            DashBoardFinActionType.OPEN_DASHBOARD_FIN_PAGE,
            RegInspectorDashBoardActionType.OPEN_REG_INSPECTOR_DASHBOARD,
            CentralInspectorDashBoardActionType.OPEN_CENTRAL_INSPECTOR_DASHBOARD_PAGE,
        ),
        mergeMap(() => {
            const queryParams: TurnoversByActivitiesQueryParams = {
                limit: TURNOVERS_LIMIT,
                sort: null, //TODO: not used for the moment `${DEFAULT_SORTING_KEY},${DEFAULT_SORTING_ORDER}`
            };

            return forkJoin({
                firstCategory: from(
                    dashBoardClient.getTurnoversByActivity({
                        ...queryParams,
                        categories: [TURNOVERS_FIRST_CATEGORY],
                    }),
                ),
                secondCategory: from(
                    dashBoardClient.getTurnoversByActivity({
                        ...queryParams,
                        categories: [TURNOVERS_SECOND_CATEGORY],
                    }),
                ),
                other: from(
                    dashBoardClient.getTurnoversByActivity({
                        ...queryParams,
                        excludeCategories: [TURNOVERS_FIRST_CATEGORY, TURNOVERS_SECOND_CATEGORY],
                    }),
                ),
            }).pipe(
                map((data) => fetchTurnoversByActivitiesSuccessAction(data)),

                catchError((err: AxiosError) =>
                    of(
                        fetchTurnoversByActivitiesErrorAction(
                            parseError(err, i18n.t('DashboardScreens.errors.failedToLoadTurnoversByActivity')),
                        ),
                    ),
                ),

                takeUntil(
                    action$.ofType(
                        DashBoardFinActionType.EXIT_DASHBOARD_FIN_PAGE,
                        CentralInspectorDashBoardActionType.EXIT_CENTRAL_INSPECTOR_DASHBOARD_PAGE,
                    ),
                ),
            );
        }),
    );
};

export const registryItemsCountInfoEpic: DashBoardFinEpic = (action$) => {
    return action$.pipe(
        ofType(
            DashBoardFinActionType.OPEN_DASHBOARD_FIN_PAGE,
            RegInspectorDashBoardActionType.OPEN_REG_INSPECTOR_DASHBOARD,
            CentralInspectorDashBoardActionType.OPEN_CENTRAL_INSPECTOR_DASHBOARD_PAGE,
        ),
        switchMap(() => {
            return from(dashBoardClient.getRegistryItemsCountInfo()).pipe(
                map((data) => fetchRegistryItemsCountSuccessAction(data)),

                catchError((err: AxiosError) =>
                    of(
                        fetchRegistryItemsCountErrorAction(
                            parseError(err, i18n.t('DashboardScreens.errors.failedToLoadRegistryItemsStat')),
                        ),
                    ),
                ),

                takeUntil(
                    action$.ofType(
                        DashBoardFinActionType.EXIT_DASHBOARD_FIN_PAGE,
                        CentralInspectorDashBoardActionType.EXIT_CENTRAL_INSPECTOR_DASHBOARD_PAGE,
                    ),
                ),
            );
        }),
    );
};

export const finChartsDataDownloadEpic: DashBoardFinEpic = (action$) => {
    return action$.pipe(
        ofType(DashBoardFinActionType.DOWNLOAD_EXCEL),
        exhaustMap((action) => {
            const fileName = FinChartsFilesMap[action.payload.dataType].fileName;

            return from(dashBoardClient.downloadFinChartData(action.payload.dataType, action.payload.params)).pipe(
                tap((fileData) => downloadDataAsFile(fileName, fileData)),
                mapTo(downloadFinChartDataAsExcelSuccessAction(action.payload.dataType)),

                catchError((err: AxiosError) =>
                    of(
                        downloadFinChartDataAsExcelErrorAction(
                            parseError(err, i18n.t('errors.failedUploadFileName', { fileName })),
                            action.payload.dataType,
                        ),
                    ),
                ),
            );
        }),
    );
};
